import { MemberAwardType, UpdateMemberAwardType, memberAwardSchema } from '@/api/mem/memberAward'
import {
    memberAwardQueryOptions,
    useUpdateMemberAwardMutation,
} from '@/api/mem/memberAwardQueryOptions'
import LoadProgress from '@/components/LoadProgress'
import EditDetailAccordion, {
    useEditDetail,
} from '@/components/detail/EditDetailForm'
import useDetailCrudEvents from '@/components/hooks/useDetailCrudEvents'
import ScoregDatePicker from '@/components/mui/ScoregDatePicker'
import { yupResolver } from '@hookform/resolvers/yup'
import { Grid, TextField } from '@mui/material'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import dayjs from 'dayjs'
import i18next from 'i18next'
import { enqueueSnackbar } from 'notistack'

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/memberaward_/$uuid',
)({
    component: MemberawardComponent,
})

function MemberawardComponent() {
    const params = Route.useParams()
    const memberAwardQuery = useSuspenseQuery(
        memberAwardQueryOptions(params.memberUuid, params.uuid),
    )
    const memberAward = memberAwardQuery.data

    const updateCrud = useDetailCrudEvents<UpdateMemberAwardType>('memberAward')

    const updateMemberAwardMutation = useUpdateMemberAwardMutation(
        memberAward.member.uuid,
        updateCrud.onUpdated,
        updateCrud.onError,
    )
    const router = useRouter()

    const onSubmit = (data: MemberAwardType) => {
        console.log('onSubmit')
        data.award = { uuid: data.award.uuid }
        updateMemberAwardMutation.mutate(data)
        enqueueSnackbar(
            i18next.t('action.changed', { entity: i18next.t('entity.award') }),
        )
        router.history.back()
    }

    const onCancel = () => {
        console.log('onCancel')
        router.history.back()
    }

    const editDetail = useEditDetail({
        onSubmit,
        onCancel,
        resolver: yupResolver<MemberAwardType, any, any>(memberAwardSchema),
        defaultValues: {
            ...memberAward,
            awardingDate: memberAward.awardingDate ? dayjs(memberAward.awardingDate) : undefined,
        },
        title: i18next.t('entity.award'),
    })

    const writeable = memberAward.permissions
        ? memberAward.permissions.some((perm) => perm === 'write')
        : false

    if (memberAwardQuery.isLoading || memberAwardQuery.isFetching) {
        return <LoadProgress />
    }

    return (
        <EditDetailAccordion {...editDetail}>
            <Grid container spacing={2}>
                <input type="hidden" {...editDetail.register('uuid')} />
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <TextField
                        defaultValue={memberAward.award.name}
                        {...editDetail.register('award.name')}
                        disabled={true}
                        label={i18next.t('entity.award')}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <TextField
                        defaultValue={memberAward.organization.name}
                        {...editDetail.register('organization.name')}
                        disabled={true}
                        label={i18next.t('entity.organization')}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <TextField
                        defaultValue={(memberAward.section && memberAward.section.name) || ''}
                        {...editDetail.register('section.name')}
                        disabled={true}
                        label={i18next.t('entity.section')}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 12, lg: 8 }}>
                    <TextField
                        defaultValue={memberAward.description}
                        {...editDetail.register('description')}
                        disabled={!writeable}
                        label={i18next.t('property.general.comment')}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <ScoregDatePicker
                        name="awardingDate"
                        defaultValue={
                            memberAward.awardingDate ? dayjs(memberAward.awardingDate) : undefined
                        }
                        disabled={!writeable}
                        control={editDetail.control}
                        label={i18next.t('property.memberAward.awardingDate')}
                    />
                </Grid>
            </Grid>
        </EditDetailAccordion>
    )
}
